import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Box, Container } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { PageTitle } from '@scoir/headers'
import { get, isEmpty } from 'lodash'

import api from 'common/api'
import features from 'common/college-features'
import useCollegeFeatures from 'common/use-college-features'
import useScid from 'common/use-scid'
import useTitle from 'common/use-title'
import { useAppCycle, useRoundConfigurationAppCycle } from 'store/application-cycle/hooks'
import { useConfigureUpcomingApplicationCycle } from 'store/feature-toggle/hooks'

import ApplicationTeaser from '../ApplicationTeaser'
import AppManagerRoleBanner from '../Configure/components/RoleBanner'
import CurrentUpcomingAppCycleToggle from '../CurrentUpcomingAppCycleToggle'
import { isIntegrationConfigured } from '../configUtils'
import { useCanViewReadOnly } from '../useCanConfigureApplications'
import ActivateApplicationStep from './components/ActivateAppStep'
import Activated from './components/Activated'
import ConfirmAppRoundsStep from './components/ConfirmAppRoundsStep'
import ConfirmConfigurationStep from './components/ConfirmConfigurationStep'
import PreviewApplicationAsStudentBanner from './components/PreviewApplicationAsStudentBanner'
import Step from './components/Step'
import TestInitiationStep from './components/TestInitiationStep'
import TestSubmissionStep from './components/TestSubmissionStep'

const useStyles = makeStyles((theme) => ({
  componentWrapper: {
    backgroundColor: get(theme, 'palette.pageBackground.main'),
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  pageHeader: {
    [theme.breakpoints.down('xs')]: {
      wordBreak: 'break-all',
    },
  },
}))

const ApplicationIntegration = () => {
  useTitle('Scoir - Activate Application')
  const styles = useStyles()
  const scid = useScid()
  const readOnlyView = useCanViewReadOnly()

  const currentAppCycle = useAppCycle()
  const upcomingCycle = useRoundConfigurationAppCycle()
  const isConfigureUpcomingEnabled = useConfigureUpcomingApplicationCycle()

  const defaultCycle = isConfigureUpcomingEnabled ? upcomingCycle : currentAppCycle
  const [focusedCycle, setApplicationCycle] = useState(defaultCycle)
  const [isCurrent, setHasCurrentFocus] = useState(isConfigureUpcomingEnabled ? false : true)

  // Need to type application config, but for now using any to avoid TS errors
  const [currentAppConfig, setCurrentAppConfig] = useState<any>(null)
  const [upcomingAppConfig, setUpcomingAppConfig] = useState<any>(null)

  const focusedAppConfig = useMemo(
    () => (isConfigureUpcomingEnabled ? (isCurrent ? currentAppConfig : upcomingAppConfig) : currentAppConfig),
    [isConfigureUpcomingEnabled, currentAppConfig, isCurrent, upcomingAppConfig],
  )

  const isApplicationIntegrationEnabled = useCollegeFeatures(features.APPLICATION_INTEGRATION)
  const showTeaser = !isApplicationIntegrationEnabled || !isIntegrationConfigured(focusedAppConfig)
  const initiationVerified =
    focusedAppConfig && focusedAppConfig.verification && focusedAppConfig.verification.initiationVerified
  const submissionVerified =
    focusedAppConfig && focusedAppConfig.verification && focusedAppConfig.verification.submissionVerified
  const configurationConfirmed =
    focusedAppConfig && focusedAppConfig.verification && focusedAppConfig.verification.configurationConfirmed
  const appRoundsConfirmed =
    focusedAppConfig && focusedAppConfig.verification && focusedAppConfig.verification.appRoundsConfirmed
  const activated = focusedAppConfig && focusedAppConfig.verification && focusedAppConfig.verification.activated

  const getCurrentAppConfig = useCallback(() => {
    api.get(`/api/colleges/${scid}/integrations/application/configuration/${currentAppCycle}`).then((response) => {
      setCurrentAppConfig(response)
    })
  }, [scid, currentAppCycle])

  const getUpcomingAppConfig = useCallback(() => {
    if (!isConfigureUpcomingEnabled) {
      return
    }

    api.get(`/api/colleges/${scid}/integrations/application/configuration/${upcomingCycle}`).then((response) => {
      setUpcomingAppConfig(response)
    })
  }, [isConfigureUpcomingEnabled, scid, upcomingCycle])

  const refreshFocusedAppConfig = useCallback(() => {
    if (isCurrent) {
      getCurrentAppConfig()
    } else {
      getUpcomingAppConfig()
    }
  }, [isCurrent, getCurrentAppConfig, getUpcomingAppConfig])

  useEffect(() => {
    getCurrentAppConfig()
    getUpcomingAppConfig()
  }, [getCurrentAppConfig, getUpcomingAppConfig])

  const canToggle = isConfigureUpcomingEnabled && !isEmpty(upcomingAppConfig?.id) && !isEmpty(currentAppConfig?.id)
  const showPreviewBanner = readOnlyView && submissionVerified && !configurationConfirmed

  if (focusedAppConfig && showTeaser) {
    return <ApplicationTeaser />
  }
  return (
    <Box
      className={styles.componentWrapper}
      px={[0, 3, 5]}
      py={5}
    >
      <Container maxWidth="lg">
        <AppManagerRoleBanner />
        {showPreviewBanner && <PreviewApplicationAsStudentBanner configurationId={focusedAppConfig?.id} />}
        {focusedAppConfig && !showTeaser && (
          <React.Fragment>
            <PageTitle
              title={`Activate ${focusedCycle - 1}-${focusedCycle} Application`}
              titleProps={{ className: styles.pageHeader }}
              actions={
                canToggle && (
                  <CurrentUpcomingAppCycleToggle
                    hasCurrentFocus={isCurrent}
                    onChangeCycleFocus={(isFocused) => {
                      setHasCurrentFocus(isFocused)
                      setApplicationCycle(isFocused ? currentAppCycle : upcomingCycle)
                    }}
                  />
                )
              }
            />
            {activated ? (
              <Activated
                applicationCycle={focusedAppConfig.applicationCycle}
                configurationId={focusedAppConfig?.id}
              />
            ) : (
              <React.Fragment>
                <Step
                  stepHeader="Step 1: Test Application Initiation"
                  cardHeader="Send and Verify Mock Application"
                  successMessage="Application Initiated"
                  isActive={!initiationVerified}
                  isComplete={Boolean(initiationVerified)}
                  refetchAppConfig={refreshFocusedAppConfig}
                  renderStep={({ completeStep }: { completeStep: () => void }) => (
                    <TestInitiationStep
                      appConfig={focusedAppConfig}
                      refetchAppConfig={refreshFocusedAppConfig}
                      completeStep={completeStep}
                    />
                  )}
                />
                <Step
                  stepHeader="Step 2: Test Application Submission"
                  cardHeader="Submit Supplement and Verify Status"
                  successMessage="Application Submitted"
                  isActive={Boolean(initiationVerified && !submissionVerified)}
                  isComplete={Boolean(submissionVerified)}
                  refetchAppConfig={refreshFocusedAppConfig}
                  renderStep={({ completeStep }: { completeStep: () => void }) => (
                    <TestSubmissionStep
                      appConfig={focusedAppConfig}
                      refetchAppConfig={refreshFocusedAppConfig}
                      completeStep={completeStep}
                    />
                  )}
                />
                <Step
                  stepHeader="Step 3: Confirm Application Configuration"
                  cardHeader="Configure College Application"
                  successMessage="Application Configuration Confirmed"
                  isActive={Boolean(submissionVerified && !configurationConfirmed)}
                  isComplete={Boolean(configurationConfirmed)}
                  refetchAppConfig={refreshFocusedAppConfig}
                  renderStep={({ completeStep }: { completeStep: () => void }) => (
                    <ConfirmConfigurationStep
                      readOnlyView={readOnlyView}
                      appConfig={focusedAppConfig}
                      completeStep={completeStep}
                    />
                  )}
                />
                <Step
                  stepHeader="Step 4: Confirm Application Rounds"
                  cardHeader="Update College App Rounds"
                  successMessage="Application Rounds Confirmed"
                  isActive={Boolean(configurationConfirmed && !appRoundsConfirmed)}
                  isComplete={Boolean(appRoundsConfirmed)}
                  refetchAppConfig={refreshFocusedAppConfig}
                  renderStep={({ completeStep }: { completeStep: () => void }) => (
                    <ConfirmAppRoundsStep
                      // @ts-expect-error not typed yet
                      appConfig={focusedAppConfig}
                      completeStep={completeStep}
                    />
                  )}
                />
                <Step
                  stepHeader="Step 5: Activate Application"
                  cardHeader="Finalize Integration Set Up"
                  successMessage="Apply with Scoir Activated"
                  secondarySuccessMessage="Your unique access link for students will be available in August."
                  isActive={Boolean(
                    initiationVerified && submissionVerified && configurationConfirmed && appRoundsConfirmed,
                  )}
                  isComplete={Boolean(activated)}
                  refetchAppConfig={refreshFocusedAppConfig}
                  renderStep={({ completeStep, isActive }: { completeStep: () => void; isActive: boolean }) => (
                    <ActivateApplicationStep
                      isActive={isActive}
                      appConfigId={focusedAppConfig?.id}
                      completeStep={completeStep}
                    />
                  )}
                />
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </Container>
    </Box>
  )
}

export default ApplicationIntegration
